import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { XIcons } from '../../primitives.jsx';
import * as XApi from '../../services.js';

export const DEFAULT_SANDBOX_OFFERING = () => ({
  providerSkuId: '',
  name: '',
  description: '',
  region: '',
  resources: {
    // 不给规格编默认值：数字看起来是「已填」的，服务商很容易直接保存，
    // 结果卖的是一台并不存在的机器。留 0，由 Adapter SKU 带出或显式填写。
    cpu: 0,
    memoryGiB: 0,
    gpuModel: '',
    gpuCount: 0,
    volumeGiB: 0,
    snapshotGiB: 0,
    environmentId: '',
    environmentName: '',
    environmentImage: '',
    environmentOs: '',
    environmentArchitecture: '',
    environmentLanguage: '',
    environmentLanguageVersion: '',
    environmentCuda: '',
    environmentFramework: '',
    environmentFrameworkVersion: '',
  },
  capabilities: {
    exec: false,
    backgroundExec: false,
    files: false,
    terminal: false,
    ports: false,
    pause: false,
    resume: false,
    stop: false,
    start: false,
    hibernate: false,
    restore: false,
    snapshot: false,
    fork: false,
    autoResume: false,
  },
  autoResumeMode: 'UNSUPPORTED',
  suspensionMechanism: 'PAUSE',
  suspensionGuarantees: {
    preservesFilesystem: true,
    preservesMemory: true,
    preservesProcesses: true,
    capacityReserved: false,
  },
  autoResumeWakeOperations: '',
  // 生命周期是 manifest 的结构化事实，不塞进只接受 boolean 的 capabilities 表单。
  // 保存时再原样合入 Offering capabilities，避免 UI round-trip 丢字段。
  lifecycle: null,
  extensionIds: '',
  extensionPricing: [],
  pricing: {
    provisioningPerHour: 0,
    // 绝不预填运行价：这是真金白银。之前默认 1（=$1/小时），服务商只要点一下
    // 「添加规格」就可能把配好的真实价格换成它。留 0，并由 schema 强制必填。
    runningPerHour: 0,
    suspendedComputePerHour: 0,
    suspendedReservationPerHour: 0,
    cpuPerHour: 0,
    memoryGiBPerHour: 0,
    gpuPerHour: 0,
    volumeGiBPerHour: 0,
    snapshotGiBPerHour: 0,
    snapshotPerOperation: 0,
  },
  rollingWindowSeconds: 300,
  shutdownGraceSeconds: 120,
  retentionWindowSeconds: 3600,
  active: true,
});

export const DEFAULT_SANDBOX_CONFIG = () => ({
  protocolVersion: '1.0',
  observationMode: 'POLLING',
  maxDetectionDelaySeconds: 60,
  webhookSecret: '',
  webhookConfigured: false,
  manifest: null,
  offerings: [DEFAULT_SANDBOX_OFFERING()],
});

const nonNegative = z.coerce.number().min(0);

export const sandboxConfigSchema = z
  .object({
    protocolVersion: z.literal('1.0'),
    observationMode: z.enum(['NATIVE_WEBHOOK', 'WATCH', 'POLLING', 'HYBRID']),
    maxDetectionDelaySeconds: z.coerce.number().int().min(1).max(300),
    webhookSecret: z.string().max(512).optional(),
    webhookConfigured: z.boolean().optional(),
    manifest: z.any().optional().nullable(),
    offerings: z
      .array(
        z
          .object({
            providerSkuId: z.string().min(1, '请填写上游 SKU ID').max(120),
            name: z.string().min(1, '请填写规格名称').max(160),
            description: z.string().max(2000).optional(),
            region: z.string().max(80).optional(),
            resources: z.object({
              cpu: nonNegative,
              memoryGiB: nonNegative,
              gpuModel: z.string().optional(),
              gpuCount: nonNegative,
              volumeGiB: nonNegative,
              snapshotGiB: nonNegative,
              environmentId: z.string().max(120).optional(),
              environmentName: z.string().max(160).optional(),
              environmentImage: z.string().max(500).optional(),
              environmentOs: z.string().max(120).optional(),
              environmentArchitecture: z.string().max(120).optional(),
              environmentLanguage: z.string().max(120).optional(),
              environmentLanguageVersion: z.string().max(120).optional(),
              environmentCuda: z.string().max(120).optional(),
              environmentFramework: z.string().max(120).optional(),
              environmentFrameworkVersion: z.string().max(120).optional(),
            }),
            capabilities: z.record(z.string(), z.boolean()),
            autoResumeMode: z.enum(['NATIVE', 'ADAPTER', 'UNSUPPORTED']),
            suspensionMechanism: z.enum(['PAUSE', 'STOP', 'HIBERNATE']),
            suspensionGuarantees: z.object({
              preservesFilesystem: z.boolean(),
              preservesMemory: z.boolean(),
              preservesProcesses: z.boolean(),
              capacityReserved: z.boolean(),
            }),
            autoResumeWakeOperations: z.string().max(2000).optional(),
            lifecycle: z.any().optional().nullable(),
            extensionIds: z.string().max(4000).optional(),
            extensionPricing: z
              .array(
                z.object({
                  extensionId: z
                    .string()
                    .regex(/^[a-z0-9][a-z0-9._-]{0,119}$/i),
                  pricePerOperation: nonNegative,
                }),
              )
              .max(100)
              .optional(),
            pricing: z.object({
              provisioningPerHour: nonNegative,
              runningPerHour: nonNegative,
              suspendedComputePerHour: nonNegative,
              suspendedReservationPerHour: nonNegative,
              cpuPerHour: nonNegative,
              memoryGiBPerHour: nonNegative,
              gpuPerHour: nonNegative,
              volumeGiBPerHour: nonNegative,
              snapshotGiBPerHour: nonNegative,
              snapshotPerOperation: nonNegative,
            }),
            rollingWindowSeconds: z.coerce.number().int().min(60).max(3600),
            shutdownGraceSeconds: z.coerce.number().int().min(30).max(600),
            retentionWindowSeconds: z.coerce.number().int().min(300).max(86400),
            active: z.boolean(),
          })
          // 一个规格必须真的能卖：有运行价、有 CPU/内存。没填完就保存的话，消费者
          // 会看到一个 $0 或规格为 0 的可售项。
          .superRefine((offering, ctx) => {
            if (!(Number(offering.pricing?.runningPerHour) > 0)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricing', 'runningPerHour'],
                message: '请填写运行价（每小时），必须大于 0。',
              });
            }
            if (!(Number(offering.resources?.cpu) > 0)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['resources', 'cpu'],
                message: '请填写 CPU 核数（或从 Adapter 选一个规格自动带出）。',
              });
            }
            if (!(Number(offering.resources?.memoryGiB) > 0)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['resources', 'memoryGiB'],
                message: '请填写内存 GiB（或从 Adapter 选一个规格自动带出）。',
              });
            }
          }),
      )
      .min(1, '至少配置一个可售规格'),
  })
  .superRefine((config, ctx) => {
    const definitions =
      config.manifest?.capabilities?.extensions ||
      config.manifest?.extensions ||
      [];
    const metered = new Set(
      definitions
        .filter(
          (extension) =>
            Array.isArray(extension?.billingMetrics) &&
            extension.billingMetrics.length > 0,
        )
        .map((extension) => extension.id),
    );
    config.offerings.forEach((offering, index) => {
      const selected = String(offering.extensionIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const rates = new Map(
        (offering.extensionPricing || []).map((rate) => [
          rate.extensionId,
          Number(rate.pricePerOperation || 0),
        ]),
      );
      selected.forEach((extensionId) => {
        if (metered.has(extensionId) && !(rates.get(extensionId) > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['offerings', index, 'extensionPricing'],
            message: `${extensionId} 声明了独立计费指标，请填写每次调用价格。`,
          });
        }
      });
    });
  });

const perSecond = (perHour) => {
  const value = Number(perHour || 0) / 3600;
  return value.toFixed(12).replace(/0+$/, '').replace(/\.$/, '') || '0';
};

const perHour = (perSecondValue) =>
  Number((Number(perSecondValue || 0) * 3600).toFixed(6));

export const buildSandboxBindingPayload = (config) => ({
  protocolVersion: '1.0',
  observationMode: config.observationMode || 'POLLING',
  maxDetectionDelaySeconds: Number(config.maxDetectionDelaySeconds || 60),
  ...(String(config.webhookSecret || '').trim()
    ? { webhookSecret: String(config.webhookSecret).trim() }
    : {}),
  offerings: (config.offerings || []).map((offering) => {
    const r = offering.resources || {};
    const p = offering.pricing || {};
    const gpuCount = Number(r.gpuCount || 0);
    const environmentId = String(r.environmentId || '').trim();
    const environment = environmentId
      ? {
          id: environmentId,
          name: String(r.environmentName || '').trim() || environmentId,
          ...(String(r.environmentImage || '').trim()
            ? { image: String(r.environmentImage).trim() }
            : {}),
          ...(String(r.environmentOs || '').trim()
            ? { os: String(r.environmentOs).trim() }
            : {}),
          ...(String(r.environmentArchitecture || '').trim()
            ? { architecture: String(r.environmentArchitecture).trim() }
            : {}),
          ...(String(r.environmentLanguage || '').trim()
            ? { language: String(r.environmentLanguage).trim() }
            : {}),
          ...(String(r.environmentLanguageVersion || '').trim()
            ? { languageVersion: String(r.environmentLanguageVersion).trim() }
            : {}),
          ...(String(r.environmentCuda || '').trim()
            ? { cuda: String(r.environmentCuda).trim() }
            : {}),
          ...(String(r.environmentFramework || '').trim()
            ? { framework: String(r.environmentFramework).trim() }
            : {}),
          ...(String(r.environmentFrameworkVersion || '').trim()
            ? { frameworkVersion: String(r.environmentFrameworkVersion).trim() }
            : {}),
        }
      : undefined;
    const resources = {
      cpu: Number(r.cpu || 0),
      memoryGiB: Number(r.memoryGiB || 0),
      volumeGiB: Number(r.volumeGiB || 0),
      snapshotGiB: Number(r.snapshotGiB || 0),
      ...(gpuCount > 0
        ? {
            gpu: [
              {
                model: String(r.gpuModel || '').trim() || 'GPU',
                count: gpuCount,
              },
            ],
          }
        : {}),
      ...(environment ? { environment } : {}),
    };
    const components = [
      ['PROVISIONING', p.provisioningPerHour],
      ['RUNNING', p.runningPerHour],
    ].map(([state, price]) => ({
      kind: 'STATE_TIME',
      state,
      category: 'OTHER',
      unit: 'SECOND',
      priceUsd: perSecond(price),
    }));
    // 挂起的「算力」和「容量保留」在 UI 上分两栏填（语义不同，方便服务商核价），
    // 但后端把 kind+state 相同的组件视为同一个计量项（category 不参与去重），
    // 分两条发会被判成重复。这里相加成一条。
    const suspendedPerHour =
      Number(p.suspendedComputePerHour || 0) +
      Number(p.suspendedReservationPerHour || 0);
    if (suspendedPerHour > 0) {
      components.push({
        kind: 'STATE_TIME',
        state: 'SUSPENDED',
        category: 'COMPUTE',
        unit: 'SECOND',
        priceUsd: perSecond(suspendedPerHour),
      });
    }
    [
      ['CPU', p.cpuPerHour],
      ['MEMORY_GIB', p.memoryGiBPerHour],
      ['VOLUME_GIB', p.volumeGiBPerHour],
      ['SNAPSHOT_GIB', p.snapshotGiBPerHour],
      ['GPU', p.gpuPerHour],
    ].forEach(([resource, price]) => {
      if (!(Number(price) > 0)) return;
      components.push({
        kind: 'RESOURCE_TIME',
        resource,
        unit: 'SECOND',
        category: ['VOLUME_GIB', 'SNAPSHOT_GIB'].includes(resource)
          ? 'STORAGE'
          : 'COMPUTE',
        ...(['CPU', 'MEMORY_GIB', 'GPU'].includes(resource)
          ? { state: 'RUNNING' }
          : {}),
        ...(resource === 'GPU' && String(r.gpuModel || '').trim()
          ? { resourceKey: String(r.gpuModel).trim() }
          : {}),
        priceUsd: perSecond(price),
      });
    });
    if (Number(p.snapshotPerOperation || 0) > 0) {
      components.push({
        kind: 'OPERATION',
        operation: 'SNAPSHOT',
        unit: 'COUNT',
        priceUsd: String(Number(p.snapshotPerOperation)),
      });
    }
    const extensionPrices = new Map(
      (offering.extensionPricing || []).map((rate) => [
        String(rate.extensionId || '').trim(),
        Number(rate.pricePerOperation || 0),
      ]),
    );
    String(offering.extensionIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((extensionId) => {
        const operationPrice = extensionPrices.get(extensionId);
        if (!(operationPrice > 0)) return;
        components.push({
          kind: 'OPERATION',
          operation: `EXTENSION:${extensionId}`,
          unit: 'COUNT',
          priceUsd: String(operationPrice),
        });
      });
    const caps = offering.capabilities || {};
    const suspensionModes = [
      caps.pause && caps.resume ? 'PAUSE' : null,
      caps.stop && caps.start ? 'STOP' : null,
      caps.hibernate && caps.restore ? 'HIBERNATE' : null,
    ].filter(Boolean);
    const suspensionMechanism = suspensionModes.includes(
      offering.suspensionMechanism,
    )
      ? offering.suspensionMechanism
      : suspensionModes[0];
    const guarantees = offering.suspensionGuarantees || {};
    const { autoResume: _autoResume, ...primitiveCapabilities } = caps;
    const wakeOperations = caps.autoResume
      ? String(offering.autoResumeWakeOperations || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      : [];
    return {
      providerSkuId: String(offering.providerSkuId || '').trim(),
      name: String(offering.name || '').trim(),
      description: String(offering.description || '').trim() || undefined,
      region: String(offering.region || '').trim() || undefined,
      resources,
      capabilities: {
        ...primitiveCapabilities,
        ...(offering.lifecycle ? { lifecycle: offering.lifecycle } : {}),
        suspension: {
          supported: !!suspensionMechanism,
          ...(suspensionMechanism ? { mechanism: suspensionMechanism } : {}),
          preservesFilesystem: !!guarantees.preservesFilesystem,
          preservesMemory: !!guarantees.preservesMemory,
          preservesProcesses: !!guarantees.preservesProcesses,
          capacityReserved: !!guarantees.capacityReserved,
          resumeSupported: !!suspensionMechanism,
          resumeOnAccessSupported: !!caps.autoResume,
          resumeOnAccessMode: caps.autoResume
            ? offering.autoResumeMode === 'ADAPTER'
              ? 'ADAPTER'
              : 'NATIVE'
            : 'UNSUPPORTED',
          wakeOperations,
        },
        extensionIds: String(offering.extensionIds || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      },
      rateCard: { version: 2, currency: 'USD', components },
      rollingWindowSeconds: 300,
      shutdownGraceSeconds: Number(offering.shutdownGraceSeconds || 120),
      retentionWindowSeconds: Number(offering.retentionWindowSeconds || 3600),
      active: offering.active !== false,
    };
  }),
});

export const transformSandboxBindingToForm = (binding) => {
  if (!binding) return DEFAULT_SANDBOX_CONFIG();
  return {
    protocolVersion: '1.0',
    observationMode: binding.observationMode || 'POLLING',
    maxDetectionDelaySeconds: Number(binding.maxDetectionDelaySeconds || 60),
    webhookSecret: '',
    webhookConfigured: !!binding.webhookConfigured,
    // 接入状态与失败原因。后端一直老实写着 lastError（2000 字符），但前端从来没读过，
    // 于是核验失败时服务商只看到服务「保存成功」却始终不在目录里，毫无线索。
    status: binding.status || null,
    lastError: binding.lastError || null,
    manifest: binding.manifest || null,
    offerings:
      Array.isArray(binding.offerings) && binding.offerings.length
        ? binding.offerings.map((offering) => {
            const resources = offering.resources || {};
            const gpu = Array.isArray(resources.gpu) ? resources.gpu[0] : null;
            const components = offering.rateCard?.components || [];
            const statePrice = (state) =>
              perHour(
                components.find(
                  (c) => c.kind === 'STATE_TIME' && c.state === state,
                )?.priceUsd,
              );
            const stateCategoryPrice = (state, categories) =>
              perHour(
                components
                  .filter(
                    (c) =>
                      c.kind === 'STATE_TIME' &&
                      c.state === state &&
                      categories.includes(c.category || 'OTHER'),
                  )
                  .reduce(
                    (sum, component) => sum + Number(component.priceUsd || 0),
                    0,
                  ),
              );
            const capabilityInput = offering.capabilities || {};
            const suspensionInput = capabilityInput.suspension || {};
            const suspensionMechanism =
              suspensionInput.mechanism ||
              suspensionInput.defaultMode ||
              suspensionInput.modes?.[0] ||
              'PAUSE';
            const legacySuspendedState = {
              PAUSE: 'PAUSED',
              STOP: 'STOPPED',
              HIBERNATE: 'HIBERNATED',
            }[suspensionMechanism];
            const resumeOnAccessSupported =
              suspensionInput.resumeOnAccessSupported === true ||
              capabilityInput.autoResume?.supported === true;
            const emptyCapabilities = Object.fromEntries(
              Object.keys(DEFAULT_SANDBOX_OFFERING().capabilities).map(
                (key) => [key, false],
              ),
            );
            return {
              providerSkuId: offering.providerSkuId || '',
              name: offering.name || '',
              description: offering.description || '',
              region: offering.region || '',
              resources: {
                cpu: Number(resources.cpu || 0),
                memoryGiB: Number(resources.memoryGiB || 0),
                gpuModel: gpu?.model || '',
                gpuCount: Number(gpu?.count || 0),
                volumeGiB: Number(resources.volumeGiB || 0),
                snapshotGiB: Number(resources.snapshotGiB || 0),
                environmentId: resources.environment?.id || '',
                environmentName: resources.environment?.name || '',
                environmentImage: resources.environment?.image || '',
                environmentOs: resources.environment?.os || '',
                environmentArchitecture:
                  resources.environment?.architecture || '',
                environmentLanguage: resources.environment?.language || '',
                environmentLanguageVersion:
                  resources.environment?.languageVersion || '',
                environmentCuda: resources.environment?.cuda || '',
                environmentFramework: resources.environment?.framework || '',
                environmentFrameworkVersion:
                  resources.environment?.frameworkVersion || '',
              },
              capabilities: {
                // Existing offerings must round-trip exactly. New-offering defaults
                // may suggest common capabilities, but missing persisted flags are
                // not evidence that the verified Adapter supports them.
                ...emptyCapabilities,
                ...Object.fromEntries(
                  Object.entries(capabilityInput).filter(
                    ([, value]) => typeof value === 'boolean',
                  ),
                ),
                autoResume: resumeOnAccessSupported,
              },
              autoResumeMode:
                suspensionInput.resumeOnAccessMode ||
                capabilityInput.autoResume?.mode ||
                'UNSUPPORTED',
              suspensionMechanism,
              suspensionGuarantees: {
                preservesFilesystem:
                  suspensionInput.preservesFilesystem ?? true,
                preservesMemory:
                  suspensionInput.preservesMemory ??
                  suspensionMechanism === 'PAUSE',
                preservesProcesses:
                  suspensionInput.preservesProcesses ??
                  suspensionMechanism === 'PAUSE',
                capacityReserved: suspensionInput.capacityReserved ?? false,
              },
              autoResumeWakeOperations: Array.isArray(
                suspensionInput.wakeOperations ||
                  capabilityInput.autoResume?.wakeOperations,
              )
                ? (
                    suspensionInput.wakeOperations ||
                    capabilityInput.autoResume.wakeOperations
                  ).join(', ')
                : '',
              lifecycle: capabilityInput.lifecycle || null,
              extensionIds: Array.isArray(capabilityInput.extensionIds)
                ? capabilityInput.extensionIds.join(', ')
                : '',
              extensionPricing: (capabilityInput.extensionIds || []).map(
                (extensionId) => ({
                  extensionId,
                  pricePerOperation: Number(
                    components.find(
                      (component) =>
                        component.kind === 'OPERATION' &&
                        component.operation === `EXTENSION:${extensionId}`,
                    )?.priceUsd || 0,
                  ),
                }),
              ),
              pricing: {
                provisioningPerHour: statePrice('PROVISIONING'),
                runningPerHour: statePrice('RUNNING'),
                suspendedComputePerHour: stateCategoryPrice('SUSPENDED', [
                  'COMPUTE',
                ]),
                suspendedReservationPerHour:
                  stateCategoryPrice('SUSPENDED', [
                    'CAPACITY_RESERVATION',
                    'OTHER',
                  ]) || statePrice(legacySuspendedState),
                cpuPerHour: perHour(
                  components.find(
                    (c) =>
                      c.kind === 'RESOURCE_TIME' &&
                      c.resource === 'CPU' &&
                      (!c.state || c.state === 'RUNNING'),
                  )?.priceUsd,
                ),
                memoryGiBPerHour: perHour(
                  components.find(
                    (c) =>
                      c.kind === 'RESOURCE_TIME' &&
                      c.resource === 'MEMORY_GIB' &&
                      (!c.state || c.state === 'RUNNING'),
                  )?.priceUsd,
                ),
                gpuPerHour: perHour(
                  components.find(
                    (c) =>
                      c.kind === 'RESOURCE_TIME' &&
                      c.resource === 'GPU' &&
                      (!c.state || c.state === 'RUNNING'),
                  )?.priceUsd,
                ),
                volumeGiBPerHour: perHour(
                  components.find(
                    (c) =>
                      c.kind === 'RESOURCE_TIME' && c.resource === 'VOLUME_GIB',
                  )?.priceUsd,
                ),
                snapshotGiBPerHour: perHour(
                  components.find(
                    (c) =>
                      c.kind === 'RESOURCE_TIME' &&
                      c.resource === 'SNAPSHOT_GIB',
                  )?.priceUsd,
                ),
                snapshotPerOperation: Number(
                  components.find(
                    (c) => c.kind === 'OPERATION' && c.operation === 'SNAPSHOT',
                  )?.priceUsd || 0,
                ),
              },
              rollingWindowSeconds: Number(
                offering.rollingWindowSeconds || 300,
              ),
              shutdownGraceSeconds: Number(
                offering.shutdownGraceSeconds || 120,
              ),
              retentionWindowSeconds: Number(
                offering.retentionWindowSeconds || 3600,
              ),
              active: offering.active !== false,
            };
          })
        : [DEFAULT_SANDBOX_OFFERING()],
  };
};

const Card = ({ children, title, subtitle, action }) => (
  <section
    style={{
      border: '1px solid var(--line)',
      borderRadius: 10,
      background: 'var(--bg-1)',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        padding: '14px 16px',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
        {subtitle && (
          <div
            style={{
              marginTop: 4,
              color: 'var(--fg-3)',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {action}
    </div>
    <div style={{ padding: 16 }}>{children}</div>
  </section>
);

const Input = React.forwardRef(({ label, hint, error, ...props }, ref) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{label}</span>
    <input ref={ref} className="input" {...props} />
    {hint && (
      <span style={{ fontSize: 11, color: 'var(--fg-4)', lineHeight: 1.45 }}>
        {hint}
      </span>
    )}
    {error && (
      <span style={{ fontSize: 11, color: 'var(--danger, #e5484d)' }}>
        {error.message}
      </span>
    )}
  </label>
));
Input.displayName = 'SandboxInput';

const Toggle = ({ control, name, label, disabled = false }) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <button
        type="button"
        className={`btn btn-sm ${field.value ? 'btn-primary' : ''}`}
        disabled={disabled}
        onClick={() => field.onChange(!field.value)}
      >
        {field.value ? <XIcons.IconCheck size={11} /> : null}
        {label}
      </button>
    )}
  />
);

// basePath 让同一套编辑器既能挂在服务级 form（默认 'sandboxConfig'，旧位置），也能挂在
// 端点 form（`endpoints.${i}.sandboxConfig`，billingType=SANDBOX 的注册入口）。
const BINDING_STATUS_LABEL = {
  DRAFT: '待核验',
  VERIFYING: '核验中',
  ACTIVE: '已上架',
  SUSPENDED: '已下架',
};

export const SandboxProviderSection = ({
  bindingStatus,
  bindingError,
  onVerify,
  verifying = false,
  basePath = 'sandboxConfig',
}) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const offerings = useFieldArray({ control, name: `${basePath}.offerings` });
  const webhookConfigured = watch(`${basePath}.webhookConfigured`);
  const manifest = watch(`${basePath}.manifest`);
  const manifestCapabilities = manifest?.capabilities || {};
  const manifestEnvironments = Array.isArray(manifest?.environments)
    ? manifest.environments
    : [];
  const manifestSkus = Array.isArray(manifest?.skus) ? manifest.skus : [];
  const baseUrl = watch('baseUrl');

  // manifest 只有核验成功时才会存进 binding，所以首次配置时表单里没有它 ——
  // 「从 Adapter 选规格」的下拉出不来，服务商只能手抄 SKU ID 和规格。而手抄正是
  // 这个设计要避免的：manifest 声明的资源与真实 SKU 常有出入（实测 e2b manifest
  // 报 4 GiB、base 模板实际 0.5 GiB），抄错了要到跑起来才发现。
  // 这里在缺 manifest 时自己探测一次补上；探测读的是 Adapter 的公开 manifest，不需要凭证。
  React.useEffect(() => {
    if (manifest || !/^https?:\/\/.+/i.test(String(baseUrl || '').trim()))
      return undefined;
    let cancelled = false;
    (async () => {
      try {
        const probed = await XApi.XApi.apiSvc.probeSandboxAdapter(
          String(baseUrl).trim(),
        );
        if (!cancelled && probed?.detected) {
          // 不标 dirty：这是补齐只读参考数据，不是服务商的编辑。
          setValue(`${basePath}.manifest`, probed, { shouldDirty: false });
        }
      } catch {
        // 探测失败就保持手填模式，不打扰用户。
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manifest, baseUrl, basePath, setValue]);
  const sandboxErrors =
    basePath.split('.').reduce((node, key) => node?.[key], errors) || {};

  // 手动规格模式：Adapter 没发布 SKU，或声明与实测不符时逐个字段填。
  const [manualSpec, setManualSpec] = React.useState({});
  const manifestSupportsGpu = manifestCapabilities.gpu === true;
  const manifestExtensions =
    manifestCapabilities.extensions || manifest?.extensions || [];
  const manifestExtensionIds = manifestExtensions
    .map((item) => item?.id)
    .filter(Boolean);

  const offeringAt = (index) => watch(`${basePath}.offerings.${index}`) || {};

  /** 一行读完卖的是什么机器。 */
  const specSummary = (index) => {
    const res = offeringAt(index).resources || {};
    const parts = [];
    if (Number(res.cpu) > 0) parts.push(`${res.cpu} vCPU`);
    if (Number(res.memoryGiB) > 0) parts.push(`${res.memoryGiB} GiB 内存`);
    if (Number(res.gpuCount) > 0)
      parts.push(`${res.gpuCount} × ${res.gpuModel || 'GPU'}`);
    else parts.push('无 GPU');
    if (Number(res.volumeGiB) > 0) parts.push(`${res.volumeGiB} GiB 持久卷`);
    return parts.join(' · ') || '—';
  };

  const CAPABILITY_LABELS = {
    exec: '执行',
    backgroundExec: '后台执行',
    files: '文件',
    ports: '端口',
    terminal: '终端',
    snapshot: '快照',
    fork: '克隆',
    autoResume: '访问时唤醒',
  };

  const enabledCapabilityLabels = (index) => {
    const caps = offeringAt(index).capabilities || {};
    const labels = Object.entries(CAPABILITY_LABELS)
      .filter(([key]) => caps[key] === true)
      .map(([, label]) => label);
    if (suspendable(index)) labels.push('挂起/恢复');
    return labels.join(' / ');
  };

  /** 三种挂起原语任一成对，就算这个规格能挂起。 */
  const suspendable = (index) => {
    const caps = offeringAt(index).capabilities || {};
    return (
      (caps.pause && caps.resume) ||
      (caps.stop && caps.start) ||
      (caps.hibernate && caps.restore)
    );
  };

  const selectedExtensions = (index) =>
    String(offeringAt(index).extensionIds || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const toggleExtension = (index, extensionId) => {
    const current = selectedExtensions(index);
    const next = current.includes(extensionId)
      ? current.filter((id) => id !== extensionId)
      : [...current, extensionId];
    setValue(`${basePath}.offerings.${index}.extensionIds`, next.join(', '), {
      shouldDirty: true,
    });
    const rates = offeringAt(index).extensionPricing || [];
    if (!rates.some((rate) => rate.extensionId === extensionId)) {
      setValue(
        `${basePath}.offerings.${index}.extensionPricing`,
        [...rates, { extensionId, pricePerOperation: 0 }],
        { shouldDirty: true },
      );
    }
  };

  /** 消费者会看到的运行价：把服务商填的分项加总，避免「填完不知道卖多少钱」。 */
  const estimatedHourly = (index) => {
    const offering = offeringAt(index);
    const p = offering.pricing || {};
    const res = offering.resources || {};
    const total =
      Number(p.runningPerHour || 0) +
      Number(p.cpuPerHour || 0) * Number(res.cpu || 0) +
      Number(p.memoryGiBPerHour || 0) * Number(res.memoryGiB || 0) +
      Number(p.gpuPerHour || 0) * Number(res.gpuCount || 0) +
      Number(p.volumeGiBPerHour || 0) * Number(res.volumeGiB || 0);
    if (!Number.isFinite(total) || total <= 0) return null;
    return Number(total.toFixed(6));
  };

  const adoptManifestCapabilities = (path) => {
    Object.keys(DEFAULT_SANDBOX_OFFERING().capabilities).forEach((key) => {
      const supported =
        key === 'autoResume'
          ? manifestCapabilities.autoResume?.supported === true
          : manifestCapabilities[key] === true;
      setValue(`${path}.capabilities.${key}`, supported, { shouldDirty: true });
    });
    const autoResume = manifestCapabilities.autoResume;
    const supportedMechanisms =
      manifestCapabilities.suspension?.supportedMechanisms ||
      manifestCapabilities.suspension?.modes ||
      [
        manifestCapabilities.pause && manifestCapabilities.resume
          ? 'PAUSE'
          : null,
        manifestCapabilities.stop && manifestCapabilities.start ? 'STOP' : null,
        manifestCapabilities.hibernate && manifestCapabilities.restore
          ? 'HIBERNATE'
          : null,
      ].filter(Boolean);
    const currentMechanism = watch(`${path}.suspensionMechanism`);
    if (
      supportedMechanisms.length &&
      !supportedMechanisms.includes(currentMechanism)
    ) {
      setValue(`${path}.suspensionMechanism`, supportedMechanisms[0], {
        shouldDirty: true,
      });
    }
    setValue(
      `${path}.autoResumeMode`,
      autoResume?.supported ? autoResume.mode : 'UNSUPPORTED',
      { shouldDirty: true },
    );
    setValue(
      `${path}.autoResumeWakeOperations`,
      Array.isArray(autoResume?.wakeOperations)
        ? autoResume.wakeOperations.join(', ')
        : '',
      { shouldDirty: true },
    );
    setValue(`${path}.lifecycle`, manifestCapabilities.lifecycle || null, {
      shouldDirty: true,
    });
    setValue(`${path}.extensionIds`, manifestExtensionIds.join(', '), {
      shouldDirty: true,
    });
    const currentRates = watch(`${path}.extensionPricing`) || [];
    setValue(
      `${path}.extensionPricing`,
      manifestExtensionIds.map((extensionId) => ({
        extensionId,
        pricePerOperation: Number(
          currentRates.find((rate) => rate.extensionId === extensionId)
            ?.pricePerOperation || 0,
        ),
      })),
      { shouldDirty: true },
    );
  };

  const adoptManifestSku = (path, sku) => {
    if (!sku) return;
    setValue(`${path}.name`, sku.name || sku.id, { shouldDirty: true });
    setValue(`${path}.description`, sku.description || '', {
      shouldDirty: true,
    });
    setValue(`${path}.region`, sku.region || '', { shouldDirty: true });
    const resources = sku.resources || {};
    const gpu = Array.isArray(resources.gpu) ? resources.gpu[0] : null;
    const environment = resources.environment || {};
    const values = {
      cpu: Number(resources.cpu || 0),
      memoryGiB: Number(resources.memoryGiB || 0),
      volumeGiB: Number(resources.volumeGiB || 0),
      snapshotGiB: Number(resources.snapshotGiB || 0),
      gpuModel: gpu?.model || '',
      gpuCount: Number(gpu?.count || 0),
      environmentId: environment.id || '',
      environmentName: environment.name || '',
      environmentImage: environment.image || '',
      environmentOs: environment.os || '',
      environmentArchitecture: environment.architecture || '',
      environmentLanguage: environment.language || '',
      environmentLanguageVersion: environment.languageVersion || '',
      environmentCuda: environment.cuda || '',
      environmentFramework: environment.framework || '',
      environmentFrameworkVersion: environment.frameworkVersion || '',
    };
    Object.entries(values).forEach(([field, value]) => {
      setValue(`${path}.resources.${field}`, value, { shouldDirty: true });
    });
  };

  return (
    <div className="col gap-3">
      <Card
        title="XAPI Sandbox Provider Protocol v1"
        subtitle="Adapter 负责把厂家状态统一为标准事件；xAPI 根据这些事件切分状态区间并完成计费。无需手工标记创建、暂停或销毁接口。"
        action={
          onVerify ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={onVerify}
              disabled={verifying}
            >
              <XIcons.IconZap size={11} />{' '}
              {verifying ? '验证中…' : '保存并验证 Adapter'}
            </button>
          ) : null
        }
      >
        {bindingStatus && (
          <div style={{ marginBottom: 14, fontSize: 12 }}>
            <div className="row gap-2">
              <span style={{ color: 'var(--fg-3)' }}>当前接入状态</span>
              <span className="tag mono">
                {BINDING_STATUS_LABEL[bindingStatus] || bindingStatus}
              </span>
            </div>
            {bindingStatus !== 'ACTIVE' && (
              <div
                style={{
                  marginTop: 8,
                  color: 'var(--warn, #b45309)',
                  lineHeight: 1.6,
                }}
              >
                {bindingError ? (
                  <>
                    核验未通过，服务暂不会出现在目录里：
                    <span className="mono">{bindingError}</span>
                  </>
                ) : (
                  '保存后会自动核验 Adapter，通过后服务才会出现在目录里。'
                )}
              </div>
            )}
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 12,
          }}
        >
          <Input label="规范版本" value="1.0" readOnly />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
              状态观察方式
            </span>
            <select
              className="input"
              {...register(`${basePath}.observationMode`)}
            >
              <option value="NATIVE_WEBHOOK">原生 Webhook</option>
              <option value="WATCH">Watch / 长轮询</option>
              <option value="POLLING">Adapter 轮询合成事件</option>
              <option value="HYBRID">Webhook + Reconcile</option>
            </select>
          </label>
          <Input
            label="最大状态发现延迟（秒）"
            type="number"
            min="1"
            max="300"
            {...register(`${basePath}.maxDetectionDelaySeconds`, {
              valueAsNumber: true,
            })}
            error={sandboxErrors.maxDetectionDelaySeconds}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <Input
            label="Webhook HMAC 密钥"
            type="password"
            placeholder={
              webhookConfigured
                ? '已配置；留空保持不变'
                : 'Adapter 与 xAPI 共用的签名密钥'
            }
            hint="必须与 Adapter 上配置的是同一个值 —— xAPI 用它验签，两边不一致会导致推送的状态事件被拒。自动配置生成的只是占位，请改成你 Adapter 的实际密钥。若 Adapter 走轮询（POLLING）而非 Webhook 推送，此项暂时用不到，但仍需填写。"
            {...register(`${basePath}.webhookSecret`)}
            error={sandboxErrors.webhookSecret}
          />
        </div>
      </Card>

      <Card
        title="可售 Sandbox 规格与 SANDBOX_METERED 费率组"
        subtitle="每个 Offering 固定资源、能力语义和价格。费率只绑定标准状态/资源/操作，Adapter 无权直接上报应扣金额。"
        action={
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => offerings.append(DEFAULT_SANDBOX_OFFERING())}
          >
            <XIcons.IconPlus size={11} /> 添加规格
          </button>
        }
      >
        <div className="col gap-3">
          {offerings.fields.map((row, index) => {
            const path = `${basePath}.offerings.${index}`;
            const error = sandboxErrors.offerings?.[index] || {};
            return (
              <div
                key={row.id}
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: 14,
                  background: 'var(--bg-2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 14,
                  }}
                >
                  <div className="micro">OFFERING {index + 1}</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost btn-icon"
                    aria-label={`删除规格 ${index + 1}`}
                    title={`删除规格 ${index + 1}`}
                    disabled={offerings.fields.length === 1}
                    onClick={() => offerings.remove(index)}
                  >
                    <XIcons.IconTrash size={12} />
                  </button>
                </div>
                {/* ── 规格：能从 Manifest 拿的一律不问 ────────────────────
                    Adapter 已经声明了 SKU 的资源与能力，再让服务商手抄一遍只会
                    抄错（实测 e2b manifest 报 4 GiB、真实是 0.5 GiB）。这里默认
                    只让人选一个 SKU，字段自动带出且只读；要改再展开手填。 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}
                >
                  {manifestSkus.length > 0 && !manualSpec[index] ? (
                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                        从 Adapter 选规格
                      </span>
                      <select
                        className="input"
                        {...register(`${path}.providerSkuId`, {
                          onChange: (event) => {
                            const sku = manifestSkus.find(
                              (item) => item.id === event.target.value,
                            );
                            adoptManifestSku(path, sku);
                            adoptManifestCapabilities(path);
                          },
                        })}
                      >
                        <option value="">请选择</option>
                        {manifestSkus.map((sku) => (
                          <option key={sku.id} value={sku.id}>
                            {sku.name || sku.id}
                          </option>
                        ))}
                      </select>
                      {error.providerSkuId && (
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--danger, #e5484d)',
                          }}
                        >
                          {error.providerSkuId.message}
                        </span>
                      )}
                    </label>
                  ) : (
                    <Input
                      label="上游规格标识（Adapter 内部）"
                      placeholder="e2b-standard"
                      hint="只由服务商配置；消费者看不到。"
                      {...register(`${path}.providerSkuId`)}
                      error={error.providerSkuId}
                    />
                  )}
                  <Input
                    label="对外规格名称"
                    placeholder="Code Sandbox 2C4G"
                    {...register(`${path}.name`)}
                    error={error.name}
                  />
                </div>

                {/* 选中 SKU 后的只读摘要：服务商一眼确认卖的是什么 */}
                {!manualSpec[index] && (
                  <div
                    className="row between"
                    style={{
                      marginTop: 10,
                      padding: '10px 12px',
                      borderRadius: 6,
                      background: 'var(--bg-1)',
                      border: '1px solid var(--line)',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--fg-2)',
                        lineHeight: 1.7,
                      }}
                    >
                      <div className="mono">{specSummary(index)}</div>
                      <div style={{ color: 'var(--fg-4)', fontSize: 11 }}>
                        {enabledCapabilityLabels(index) || '尚未选择规格'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() =>
                        setManualSpec((prev) => ({ ...prev, [index]: true }))
                      }
                    >
                      手动填写规格
                    </button>
                  </div>
                )}

                {/* 手动模式：Adapter 没发布 SKU、或声明与实测不符时的逃生口 */}
                {manualSpec[index] && (
                  <>
                    <div
                      className="row between"
                      style={{ margin: '14px 0 8px', alignItems: 'center' }}
                    >
                      <div className="micro">手动规格</div>
                      {manifestSkus.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() =>
                            setManualSpec((prev) => ({
                              ...prev,
                              [index]: false,
                            }))
                          }
                        >
                          改回从 Adapter 选
                        </button>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 12,
                      }}
                    >
                      <Input
                        label="CPU"
                        type="number"
                        min="0"
                        step="1"
                        {...register(`${path}.resources.cpu`, {
                          valueAsNumber: true,
                        })}
                        error={error.resources?.cpu}
                      />
                      <Input
                        label="内存 GiB"
                        type="number"
                        min="0"
                        step="0.5"
                        {...register(`${path}.resources.memoryGiB`, {
                          valueAsNumber: true,
                        })}
                        error={error.resources?.memoryGiB}
                      />
                      <Input
                        label="持久卷 GiB"
                        type="number"
                        min="0"
                        step="1"
                        {...register(`${path}.resources.volumeGiB`, {
                          valueAsNumber: true,
                        })}
                      />
                      <Input
                        label="区域"
                        placeholder="us-east"
                        {...register(`${path}.region`)}
                        error={error.region}
                      />
                      <Input
                        label="规格说明"
                        placeholder="适合代码执行"
                        {...register(`${path}.description`)}
                        error={error.description}
                      />
                      <Input
                        label="快照存储 GiB"
                        type="number"
                        min="0"
                        step="1"
                        {...register(`${path}.resources.snapshotGiB`, {
                          valueAsNumber: true,
                        })}
                      />
                      {/* GPU 只在 Adapter 声明支持时才问 —— 给纯 CPU 沙箱看 CUDA/框架是纯噪音 */}
                      {manifestSupportsGpu && (
                        <>
                          <Input
                            label="GPU 型号"
                            placeholder="A100"
                            {...register(`${path}.resources.gpuModel`)}
                          />
                          <Input
                            label="GPU 数量"
                            type="number"
                            min="0"
                            step="1"
                            {...register(`${path}.resources.gpuCount`, {
                              valueAsNumber: true,
                            })}
                          />
                          <Input
                            label="CUDA"
                            placeholder="12.4"
                            {...register(`${path}.resources.environmentCuda`)}
                          />
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 12,
                        marginTop: 12,
                      }}
                    >
                      {manifestEnvironments.length > 0 ? (
                        <label
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                            运行环境
                          </span>
                          <select
                            className="input"
                            {...register(`${path}.resources.environmentId`, {
                              onChange: (event) => {
                                const env = manifestEnvironments.find(
                                  (item) => item.id === event.target.value,
                                );
                                if (env)
                                  setValue(
                                    `${path}.resources.environmentName`,
                                    env.name || env.id,
                                    { shouldDirty: true },
                                  );
                              },
                            })}
                          >
                            <option value="">不声明</option>
                            {manifestEnvironments.map((env) => (
                              <option key={env.id} value={env.id}>
                                {env.name || env.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <Input
                          label="环境 ID"
                          placeholder="python-3.12"
                          {...register(`${path}.resources.environmentId`)}
                        />
                      )}
                      <Input
                        label="环境名称"
                        placeholder="Python 3.12"
                        {...register(`${path}.resources.environmentName`)}
                      />
                      <Input
                        label="镜像"
                        placeholder="registry/image@sha256:…"
                        {...register(`${path}.resources.environmentImage`)}
                      />
                    </div>

                    <div
                      className="row between"
                      style={{ margin: '14px 0 8px', alignItems: 'center' }}
                    >
                      <div className="micro">
                        能力（只能勾选 Adapter 已声明的）
                      </div>
                      {manifest && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => adoptManifestCapabilities(path)}
                        >
                          全部采用 Manifest
                        </button>
                      )}
                    </div>
                    <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                      {[
                        ['exec', 'Exec'],
                        ['backgroundExec', 'Background Exec'],
                        ['files', 'Files'],
                        ['terminal', 'Terminal'],
                        ['ports', 'Ports'],
                        ['pause', 'Pause'],
                        ['resume', 'Resume'],
                        ['stop', 'Stop'],
                        ['start', 'Start'],
                        ['hibernate', 'Hibernate'],
                        ['restore', 'Restore'],
                        ['snapshot', 'Snapshot'],
                        ['fork', 'Fork'],
                        ['autoResume', 'Auto Resume'],
                      ].map(([key, label]) => {
                        const supported =
                          key === 'autoResume'
                            ? manifestCapabilities.autoResume?.supported ===
                              true
                            : manifestCapabilities[key] === true;
                        if (manifest && !supported) return null;
                        return (
                          <Toggle
                            key={key}
                            control={control}
                            name={`${path}.capabilities.${key}`}
                            label={label}
                          />
                        );
                      })}
                    </div>
                    {suspendable(index) && (
                      <div style={{ maxWidth: 360, marginTop: 12 }}>
                        <label
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                            挂起机制
                          </span>
                          <select
                            className="input"
                            {...register(`${path}.suspensionMechanism`)}
                          >
                            {watch(`${path}.capabilities.pause`) &&
                              watch(`${path}.capabilities.resume`) && (
                                <option value="PAUSE">
                                  PAUSE · 保留内存，恢复最快
                                </option>
                              )}
                            {watch(`${path}.capabilities.stop`) &&
                              watch(`${path}.capabilities.start`) && (
                                <option value="STOP">
                                  STOP · 释放内存，保留磁盘
                                </option>
                              )}
                            {watch(`${path}.capabilities.hibernate`) &&
                              watch(`${path}.capabilities.restore`) && (
                                <option value="HIBERNATE">
                                  HIBERNATE · 内存落盘
                                </option>
                              )}
                          </select>
                        </label>
                      </div>
                    )}
                  </>
                )}

                {/* ── 扩展：勾选卖哪些，价格在下方自动出现 ── */}
                {manifestExtensionIds.length > 0 && (
                  <>
                    <div className="micro" style={{ margin: '18px 0 8px' }}>
                      厂家专有能力（勾选后可单独定价）
                    </div>
                    <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                      {manifestExtensionIds.map((extensionId) => {
                        const selected =
                          selectedExtensions(index).includes(extensionId);
                        return (
                          <button
                            key={extensionId}
                            type="button"
                            className={`btn btn-sm ${selected ? 'btn-primary' : ''}`}
                            onClick={() => toggleExtension(index, extensionId)}
                          >
                            {selected ? <XIcons.IconCheck size={11} /> : null}
                            {extensionId}
                          </button>
                        );
                      })}
                    </div>
                    {selectedExtensions(index).length > 0 && (
                      <div className="col gap-2" style={{ marginTop: 10 }}>
                        {selectedExtensions(index).map((extensionId) => {
                          const rates =
                            offeringAt(index).extensionPricing || [];
                          const rateIndex = rates.findIndex(
                            (rate) => rate.extensionId === extensionId,
                          );
                          const definition = manifestExtensions.find(
                            (extension) => extension.id === extensionId,
                          );
                          return rateIndex >= 0 ? (
                            <Input
                              key={extensionId}
                              label={`${extensionId} · 每次调用价格（USD）`}
                              type="number"
                              min="0"
                              step="0.000001"
                              hint={
                                definition?.billingMetrics?.length
                                  ? `该扩展有独立计费指标：${definition.billingMetrics.join(', ')}；价格必须大于 0。`
                                  : '没有独立上游成本时可以保持 0，仅按实例状态计费。'
                              }
                              {...register(
                                `${path}.extensionPricing.${rateIndex}.pricePerOperation`,
                                { valueAsNumber: true },
                              )}
                            />
                          ) : null;
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* ── 价格：按上面已声明的能力生成，用不到的不显示 ── */}
                <div className="micro" style={{ margin: '18px 0 8px' }}>
                  价格（USD，按小时；系统自动换算成秒）
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12,
                  }}
                >
                  <Input
                    label="运行中 / 小时"
                    type="number"
                    min="0"
                    step="0.0001"
                    hint="必填。实例处于 RUNNING 时按秒计费。"
                    {...register(`${path}.pricing.runningPerHour`, {
                      valueAsNumber: true,
                    })}
                    error={error.pricing?.runningPerHour}
                  />
                  {suspendable(index) && (
                    <Input
                      label="挂起 / 小时"
                      type="number"
                      min="0"
                      step="0.0001"
                      hint="挂起免费就填 0。"
                      {...register(`${path}.pricing.suspendedComputePerHour`, {
                        valueAsNumber: true,
                      })}
                      error={error.pricing?.suspendedComputePerHour}
                    />
                  )}
                  {Number(watch(`${path}.resources.volumeGiB`)) > 0 && (
                    <Input
                      label="持久卷 / GiB·小时"
                      type="number"
                      min="0"
                      step="0.000001"
                      hint="按规格里的 GiB 数自动相乘；停机后仍计费。"
                      {...register(`${path}.pricing.volumeGiBPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                  )}
                  {Number(watch(`${path}.resources.gpuCount`)) > 0 && (
                    <Input
                      label="GPU / 张·小时"
                      type="number"
                      min="0"
                      step="0.0001"
                      hint="按 GPU 张数自动相乘；默认只在运行中计费。"
                      {...register(`${path}.pricing.gpuPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                  )}
                  {watch(`${path}.capabilities.snapshot`) && (
                    <Input
                      label="快照 / 次"
                      type="number"
                      min="0"
                      step="0.0001"
                      {...register(`${path}.pricing.snapshotPerOperation`, {
                        valueAsNumber: true,
                      })}
                    />
                  )}
                </div>
                {estimatedHourly(index) !== null && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: 'var(--fg-2)',
                    }}
                  >
                    消费者看到的运行价约{' '}
                    <span
                      className="mono"
                      style={{ color: 'var(--ok, #52d3ad)' }}
                    >
                      ${estimatedHourly(index)}
                    </span>{' '}
                    / 小时
                  </div>
                )}

                {/* 少见但必要的项，收进折叠区，默认不打扰 */}
                <details style={{ marginTop: 14 }}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontSize: 12,
                      color: 'var(--fg-3)',
                    }}
                  >
                    更多计费项与风控窗口
                  </summary>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    <Input
                      label="创建中 / 小时"
                      type="number"
                      min="0"
                      step="0.0001"
                      hint="多数服务商填 0。"
                      {...register(`${path}.pricing.provisioningPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                    {suspendable(index) && (
                      <Input
                        label="挂起容量保留 / 小时"
                        type="number"
                        min="0"
                        step="0.0001"
                        hint="挂起仍占着 GPU/机位才填。"
                        {...register(
                          `${path}.pricing.suspendedReservationPerHour`,
                          { valueAsNumber: true },
                        )}
                      />
                    )}
                    <Input
                      label="快照存储 / GiB·小时"
                      type="number"
                      min="0"
                      step="0.000001"
                      {...register(`${path}.pricing.snapshotGiBPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                    <Input
                      label="CPU 核 / 小时"
                      type="number"
                      min="0"
                      step="0.000001"
                      hint="已按规格整体定价就留 0。"
                      {...register(`${path}.pricing.cpuPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                    <Input
                      label="内存 GiB / 小时"
                      type="number"
                      min="0"
                      step="0.000001"
                      hint="已按规格整体定价就留 0。"
                      {...register(`${path}.pricing.memoryGiBPerHour`, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    <Input
                      label="付费状态滚动预留（秒）"
                      type="number"
                      value="300"
                      readOnly
                      hint="固定 5 分钟。"
                    />
                    <Input
                      label="安全关停预留（秒）"
                      type="number"
                      min="30"
                      max="600"
                      {...register(`${path}.shutdownGraceSeconds`, {
                        valueAsNumber: true,
                      })}
                    />
                    <Input
                      label="零费用挂起复核（秒）"
                      type="number"
                      min="300"
                      max="86400"
                      {...register(`${path}.retentionWindowSeconds`, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
